/**
 * PancakeSwap V2 · 校验代币是否有 BNB 交易对，且池内 BNB ≥ minBnb
 */
import {
  createPublicClient,
  fallback,
  formatUnits,
  http,
  parseAbi,
  zeroAddress,
} from "viem";
import { bsc } from "viem/chains";

export const WBNB = "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";
export const USDT_BSC = "0x55d398326f99059fF775485246999027B3197955";
/** Binance-Peg USD Coin (BSC) */
export const USDC_BSC = "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d";
/** PancakeSwap V2 Factory (BSC) */
export const PANCAKE_V2_FACTORY = "0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73";
/** 池内 BNB 侧最低流动性（BNB）— 通用自定义资产 */
export const MIN_BNB_PAIR_RESERVE = 0.05;
/** 持币分红：站内硬门槛（V2 BNB 单边须严格大于此值） */
export const MIN_HOLD_DIV_BNB_RESERVE = 2;
/** 持币分红：文案建议值 */
export const RECOMMENDED_HOLD_DIV_BNB_RESERVE = 3;
/** tmlp 自选股：Pancake V2 BNB 单边须严格大于此值（对齐蝴蝶白名单 V2BNB>3） */
export const MIN_TMLP_CUSTOM_BNB_RESERVE = 3;
/** @deprecated 改用 MIN_BNB_PAIR_RESERVE */
export const MIN_BNB_PAIR_LIQ_USD = MIN_BNB_PAIR_RESERVE;

const factoryAbi = parseAbi([
  "function getPair(address tokenA, address tokenB) view returns (address pair)",
]);
const pairAbi = parseAbi([
  "function getReserves() view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
  "function token0() view returns (address)",
  "function token1() view returns (address)",
]);
const erc20Abi = parseAbi([
  "function symbol() view returns (string)",
  "function name() view returns (string)",
  "function decimals() view returns (uint8)",
]);

function publicClient() {
  return createPublicClient({
    chain: bsc,
    transport: fallback(
      [
        http("/api/rpc.php", { timeout: 20_000 }),
        http("https://bsc-dataseed.binance.org/", { timeout: 18_000 }),
        http("https://bsc.publicnode.com", { timeout: 18_000 }),
      ],
      { rank: false }
    ),
  });
}

function isAddr(a) {
  return typeof a === "string" && /^0x[a-fA-F0-9]{40}$/.test(a);
}

/** 读取 ERC20 symbol / name / decimals（自定义支付底用） */
export async function readErc20Meta(token) {
  const addr = String(token || "").trim();
  if (!isAddr(addr)) {
    return { ok: false, symbol: "", name: "", decimals: 18 };
  }
  const client = publicClient();
  let symbol = "";
  let name = "";
  let decimals = 18;
  try {
    symbol = String(
      await client.readContract({
        address: addr,
        abi: erc20Abi,
        functionName: "symbol",
      }) || ""
    );
  } catch {
    /* ignore */
  }
  try {
    name = String(
      await client.readContract({
        address: addr,
        abi: erc20Abi,
        functionName: "name",
      }) || ""
    );
  } catch {
    /* ignore */
  }
  try {
    decimals = Number(
      await client.readContract({
        address: addr,
        abi: erc20Abi,
        functionName: "decimals",
      })
    );
    if (!Number.isFinite(decimals)) decimals = 18;
  } catch {
    decimals = 18;
  }
  if (!symbol && name) symbol = name.slice(0, 12);
  if (!symbol) symbol = `${addr.slice(0, 6)}…`;
  return { ok: true, symbol, name, decimals, address: addr };
}

let bnbUsdCache = { t: 0, v: 0 };

/** BNB/USD：优先链上 WBNB-USDT V2，失败再 Binance */
export async function fetchBnbUsdPrice(client = publicClient()) {
  const now = Date.now();
  if (bnbUsdCache.v > 0 && now - bnbUsdCache.t < 60_000) return bnbUsdCache.v;

  try {
    const pair = await client.readContract({
      address: PANCAKE_V2_FACTORY,
      abi: factoryAbi,
      functionName: "getPair",
      args: [WBNB, USDT_BSC],
    });
    if (pair && pair.toLowerCase() !== zeroAddress.toLowerCase()) {
      const [token0, reserves] = await Promise.all([
        client.readContract({ address: pair, abi: pairAbi, functionName: "token0" }),
        client.readContract({ address: pair, abi: pairAbi, functionName: "getReserves" }),
      ]);
      const r0 = reserves[0];
      const r1 = reserves[1];
      const wbnbIs0 = token0.toLowerCase() === WBNB.toLowerCase();
      const wbnb = wbnbIs0 ? r0 : r1;
      const usdt = wbnbIs0 ? r1 : r0;
      if (wbnb > 0n && usdt > 0n) {
        // USDT 18 decimals on BSC
        const price =
          Number(formatUnits(usdt, 18)) / Number(formatUnits(wbnb, 18));
        if (Number.isFinite(price) && price > 0) {
          bnbUsdCache = { t: now, v: price };
          return price;
        }
      }
    }
  } catch {
    /* fall through */
  }

  try {
    const res = await fetch(
      "https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT"
    );
    if (res.ok) {
      const j = await res.json();
      const price = Number(j?.price);
      if (Number.isFinite(price) && price > 0) {
        bnbUsdCache = { t: now, v: price };
        return price;
      }
    }
  } catch {
    /* ignore */
  }

  if (bnbUsdCache.v > 0) return bnbUsdCache.v;
  return 600; // 兜底，避免全失败时无法校验
}

/**
 * @param {string} token
 * @param {{ minBnb?: number, exclusive?: boolean }} [opts]
 *   exclusive=true → 须严格大于 minBnb（持币分红硬门槛）
 * @returns {Promise<{
 *   ok: boolean,
 *   code: string,
 *   message: string,
 *   messageEn: string,
 *   token: string,
 *   pair?: string,
 *   symbol?: string,
 *   bnbReserve?: number,
 *   liqUsd?: number,
 *   bnbUsd?: number,
 * }>}
 */
export async function validateTokenBnbV2Liquidity(token, opts = {}) {
  const minBnb = Number(opts.minBnb ?? opts.minUsd ?? MIN_BNB_PAIR_RESERVE);
  const exclusive = !!opts.exclusive;
  const addr = String(token || "").trim();
  if (!isAddr(addr)) {
    return {
      ok: false,
      code: "bad_address",
      message: "请填写有效的代币合约地址",
      messageEn: "Invalid token address",
      token: addr,
    };
  }
  if (addr.toLowerCase() === WBNB.toLowerCase()) {
    return {
      ok: false,
      code: "is_wbnb",
      message: "不能把 WBNB 本身加为分红资产",
      messageEn: "Cannot add WBNB as dividend asset",
      token: addr,
    };
  }

  const client = publicClient();
  let symbol = "";
  try {
    symbol = await client.readContract({
      address: addr,
      abi: erc20Abi,
      functionName: "symbol",
    });
  } catch {
    try {
      const name = await client.readContract({
        address: addr,
        abi: erc20Abi,
        functionName: "name",
      });
      symbol = String(name || "").slice(0, 12);
    } catch {
      symbol = `${addr.slice(0, 6)}…`;
    }
  }

  let pair;
  try {
    pair = await client.readContract({
      address: PANCAKE_V2_FACTORY,
      abi: factoryAbi,
      functionName: "getPair",
      args: [addr, WBNB],
    });
  } catch (e) {
    return {
      ok: false,
      code: "rpc",
      message: `链上查询失败：${e instanceof Error ? e.message : String(e)}`,
      messageEn: "On-chain lookup failed",
      token: addr,
      symbol,
    };
  }

  if (!pair || pair.toLowerCase() === zeroAddress.toLowerCase()) {
    return {
      ok: false,
      code: "no_pair",
      message: `${symbol || "该币"} 没有 Pancake V2 的 BNB 交易对，不能添加`,
      messageEn: `${symbol || "Token"} has no Pancake V2 BNB pair`,
      token: addr,
      symbol,
    };
  }

  let token0;
  let reserves;
  try {
    [token0, reserves] = await Promise.all([
      client.readContract({ address: pair, abi: pairAbi, functionName: "token0" }),
      client.readContract({ address: pair, abi: pairAbi, functionName: "getReserves" }),
    ]);
  } catch (e) {
    return {
      ok: false,
      code: "reserves",
      message: `读取流动性失败：${e instanceof Error ? e.message : String(e)}`,
      messageEn: "Failed to read pair reserves",
      token: addr,
      symbol,
      pair,
    };
  }

  const r0 = reserves[0];
  const r1 = reserves[1];
  if (r0 <= 0n || r1 <= 0n) {
    return {
      ok: false,
      code: "empty_pool",
      message: `${symbol || "该币"} 的 BNB V2 池子为空`,
      messageEn: "BNB V2 pool is empty",
      token: addr,
      symbol,
      pair,
    };
  }

  const wbnbIs0 = token0.toLowerCase() === WBNB.toLowerCase();
  const bnbReserveWei = wbnbIs0 ? r0 : r1;
  const bnbReserve = Number(formatUnits(bnbReserveWei, 18));
  let bnbUsd = 0;
  let liqUsd = 0;
  try {
    bnbUsd = await fetchBnbUsdPrice(client);
    liqUsd = bnbReserve * bnbUsd * 2;
  } catch {
    /* 展示用，不挡校验 */
  }

  const liqOk = exclusive ? bnbReserve > minBnb : bnbReserve >= minBnb;
  const op = exclusive ? ">" : "≥";
  if (!liqOk) {
    return {
      ok: false,
      code: "low_liq",
      message: `${symbol || "该币"} BNB 侧约 ${bnbReserve.toFixed(4)} BNB，须 ${op} ${minBnb} BNB（V2 单边）`,
      messageEn: `BNB side ~${bnbReserve.toFixed(4)} BNB, need ${op} ${minBnb} BNB (V2 one-side)`,
      token: addr,
      symbol,
      pair,
      bnbReserve,
      liqUsd,
      bnbUsd,
    };
  }

  return {
    ok: true,
    code: "ok",
    message: `${symbol} · Pancake V2 BNB 对 · 池内约 ${bnbReserve.toFixed(3)} BNB`,
    messageEn: `${symbol} · Pancake V2 BNB · ~${bnbReserve.toFixed(3)} BNB in pool`,
    token: addr,
    symbol: String(symbol || "").trim() || short(addr),
    pair,
    bnbReserve,
    liqUsd,
    bnbUsd,
  };
}

function short(a) {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}
